import { createAccountsApi, type AccountsApi } from './accounts.ts'
import { buildAuthorizeUrl, buildLogoutUrl, exchangeCode } from './endpoints.ts'
import { AuthError } from './errors.ts'
import { createKernel, type AuthKernel } from './kernel.ts'
import { createMfaApi, type MfaApi } from './mfa.ts'
import { createPasswordApi, type PasswordApi } from './password.ts'
import { createPkcePair, createState } from './pkce.ts'
import type { SessionStore } from './session.ts'
import { saveTransaction, takeTransaction, verifyCallback } from './transaction.ts'
import type { AuthConfig, IdentityProvider, RefreshTokenStore, Session } from './types.ts'

// The package's public surface. Framework-agnostic on purpose — the same object serves the
// two SPAs and the Capacitor shell; the React binding is the three lines in `./react.ts`.

export interface AuthClient {
  readonly session: Pick<SessionStore, 'get' | 'subscribe'>

  /** Email and password, for admin-panel. The hosted UI knows nothing about these. */
  readonly password: PasswordApi

  /** The second factor a user enrols on their own account. Optional on the pool. */
  readonly mfa: MfaApi

  /** The accounts open on this device, and the one in use. One at a time unless `multipleAccounts`. */
  readonly accounts: AccountsApi

  /** Redirects to the hosted UI. Resolves with the URL when `navigate: false`. */
  login(options?: LoginOptions): Promise<string>

  /** Verifies `state`, exchanges the code, and says where the user meant to go. */
  completeLogin(callbackUrl: string): Promise<CallbackResult>

  /** A valid access token, refreshing when needed. Null when there is nothing to restore. */
  getAccessToken(): Promise<string | null>

  /** The Bearer credential `services/api` expects. Freshness rides on the access token's expiry. */
  getIdToken(): Promise<string | null>

  /** Forces a refresh regardless of expiry. Concurrent calls share one request. */
  refresh(): Promise<string | null>

  /** Takes the account in use off this device, then goes to the hosted UI's `/logout` so the cookie
   *  dies too. Nothing is revoked: that is `password.signOut` or `accounts.signOut`. */
  logout(options?: { readonly navigate?: boolean }): Promise<string>

  /** Restores a session from the stored refresh token on boot. */
  restore(): Promise<boolean>
}

export interface LoginOptions {
  readonly identityProvider?: IdentityProvider
  readonly loginHint?: string
  /** Where to return after login. Defaults to the current path. */
  readonly returnTo?: string
  /** False to get the URL back instead of navigating. Default true. */
  readonly navigate?: boolean
}

export interface CallbackResult {
  readonly returnTo: string
}

export interface AuthClientOptions {
  readonly config: AuthConfig
  /** Defaults to `MemoryRefreshTokenStore` — the conservative choice (see storage.ts). */
  readonly refreshTokenStore?: RefreshTokenStore
  /** Injected in tests; defaults to `globalThis.location.assign`. */
  readonly navigate?: (url: string) => void
  /** Several accounts on this device, one in use (spec #278, #302). Off: a sign-in replaces the last. */
  readonly multipleAccounts?: boolean
}

function defaultNavigate(url: string): void {
  globalThis.location.assign(url)
}

export function createAuthClient(options: AuthClientOptions): AuthClient {
  const { config } = options
  const kernel: AuthKernel = createKernel(options)
  const store = kernel.store
  const navigate = options.navigate ?? defaultNavigate

  // One completion per callback URL: StrictMode runs the callback route's effect twice in
  // dev, and the second call would find the verifier already consumed.
  let completion: { url: string; result: Promise<CallbackResult> } | null = null

  async function completeLoginOnce(callbackUrl: string): Promise<CallbackResult> {
    const params = new URL(callbackUrl).searchParams

    // The hosted UI reports its own failures on the redirect, not as an HTTP error.
    const oauthError = params.get('error')
    if (oauthError) {
      takeTransaction()
      store.reset()
      throw new AuthError(
        'callback_error',
        `The hosted UI returned "${oauthError}"${
          params.get('error_description') ? `: ${params.get('error_description')}` : ''
        }`,
      )
    }

    // Read (and clear) before validating: a rejected callback must not leave a
    // reusable verifier behind.
    const transaction = takeTransaction()
    let verified
    try {
      verified = verifyCallback(transaction, params.get('state'))
    } catch (error) {
      store.reset()
      throw error
    }

    const code = params.get('code')
    if (!code) {
      store.reset()
      throw new AuthError('callback_error', 'The callback carried no authorization code')
    }

    store.setAuthenticating()
    try {
      await kernel.applyTokens(await exchangeCode(config, { code, verifier: verified.verifier }))
    } catch (error) {
      await kernel.abandonSignIn()
      throw error
    }
    return { returnTo: verified.returnTo }
  }

  return {
    session: { get: store.get, subscribe: store.subscribe },

    password: createPasswordApi(kernel, navigate),

    mfa: createMfaApi(kernel),

    accounts: createAccountsApi(kernel),

    async login(loginOptions = {}) {
      const { verifier, challenge } = await createPkcePair()
      const state = createState()
      const returnTo =
        loginOptions.returnTo ??
        `${globalThis.location?.pathname ?? '/'}${globalThis.location?.search ?? ''}`

      saveTransaction({ verifier, state, returnTo })
      store.setAuthenticating()

      const url = buildAuthorizeUrl(config, {
        challenge,
        state,
        identityProvider: loginOptions.identityProvider,
        loginHint: loginOptions.loginHint,
      })
      if (loginOptions.navigate !== false) navigate(url)
      return url
    },

    completeLogin(callbackUrl) {
      if (completion?.url !== callbackUrl) {
        completion = { url: callbackUrl, result: completeLoginOnce(callbackUrl) }
      }
      return completion.result
    },

    getAccessToken: kernel.accessToken,

    async getIdToken() {
      // The access token's validity window doubles as the id_token's freshness check:
      // the two arrive in the same response with the same configured lifetime.
      if (store.validAccessToken()) return store.get().idToken
      const refreshed = await kernel.refresh()
      return refreshed === null ? null : store.get().idToken
    },

    refresh: kernel.refresh,

    async logout(logoutOptions = {}) {
      await kernel.forgetActive()
      const url = buildLogoutUrl(config)
      if (logoutOptions.navigate !== false) navigate(url)
      return url
    },

    async restore() {
      if (store.validAccessToken()) return true
      // No probe before the refresh: `runRefresh` already answers "nothing stored" by
      // resetting, and on native every read of the store is a Keychain round trip.
      store.setAuthenticating()
      try {
        return (await kernel.refresh()) !== null
      } catch {
        // Boot must not throw on a dead token: an expired session is the normal case for a
        // returning visitor, and the kernel's refresh already took that account away.
        return false
      }
    },
  }
}

// The token half of `@volee/api-client`'s `TokenProvider`. Structural on purpose: importing
// the type would couple the two packages, which meet only in each app's `services/api.ts`.
export function idTokenProvider(client: AuthClient): {
  getToken: () => Promise<string | null>
  refresh: () => Promise<string | null>
} {
  return {
    getToken: () => client.getIdToken(),
    refresh: async () => {
      const refreshed = await client.refresh()
      // Read straight from the session: getIdToken() would re-run the 30s skew check on a
      // just-issued token and could spend a second, un-coalesced rotation.
      return refreshed === null ? null : client.session.get().idToken
    },
  }
}

export type { Session }
